import React, {useEffect, useRef} from 'react';
import Header from '../Header';
import Loading from '../Loading';
import _ from 'lodash';
import {
  ICountry,
  ILang,
  IState,
  LocationCategory,
  LocationType,
  ProductCategory,
  Replacement,
} from '../../interface';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import actions from '../../redux/actions';
import useURLParameter from '../../hooks/use-url-parameter';
import {
  getCountrySetting,
  getLocationCategories,
  getLocationTypes,
  getProductCategories,
} from '../../services';
import {useLocation, useSearchParams} from 'react-router-dom';
import {GLOBALFONTFAMILY} from '../../constants';
import { Variant } from '../../constants/variants';
import {COUNTRIES} from '../../constants/countries';
import {LANGUAGES} from '../../constants/languages';

interface Props {
  lang: string;
  setLang: (value: string) => void;
  langs: ILang[];
  setLangs: (value: ILang[]) => void;
  replacements: Replacement;
  setReplacements: (value: Replacement) => void;
  countries: ICountry[];
  setCountries: (value: ICountry[]) => void;
  setCountry: (value: ICountry) => void;
  setWhereToBuyEnabled: (value: boolean) => void;
  whereToBuyEnabled: boolean | null;
  setWhereToServiceEnabled: (value: boolean) => void;
  whereToServiceEnabled: boolean | null;
  setEnableWtbDistanceFilter: (value: boolean) => void;
  enableWtbDistanceFilter: boolean | null;
  setHasCastrolCertifiedWorkshops: (value: boolean) => void;
  locationTypesList: LocationType[];
  setLocationTypesList: (value: LocationType[]) => void;
  locationCategoriesList: LocationCategory[];
  setLocationCatetoriesList: (value: LocationCategory[]) => void;
  productCategoriesList: ProductCategory[];
  setProductCatetoriesList: (value: ProductCategory[]) => void;
  setIsMiles: (value: boolean) => void;
  setDistanceInMilesOrKM: (value: number) => void;
  children: React.ReactNode;
}

function WithLangs(props: Props) {
  const {
    lang,
    setLang,
    langs,
    setLangs,
    replacements,
    setReplacements,
    countries,
    setCountries,
    setCountry,
    setWhereToBuyEnabled,
    whereToBuyEnabled,
    setWhereToServiceEnabled,
    whereToServiceEnabled,
    setEnableWtbDistanceFilter,
    enableWtbDistanceFilter,
    setHasCastrolCertifiedWorkshops,
    locationTypesList,
    setLocationTypesList,
    locationCategoriesList,
    setLocationCatetoriesList,
    productCategoriesList,
    setProductCatetoriesList,
    setIsMiles,
    setDistanceInMilesOrKM,
    children,
  } = props;
  const {languageIsoCode, countryIsoCode} = useURLParameter(
    location.pathname,
    countries,
    langs,
  );
  useEffect(() => {
    if (!_.isEmpty(countries)) {
      if (localStorage.getItem('countryCode')) {
        setCountry(
          _.find(countries, [
            'isoCode',
            localStorage.getItem('countryCode'),
          ]) as ICountry,
        );
      } else {
        if (countryIsoCode) {
          setCountry(
            _.find(countries, ['isoCode', countryIsoCode]) as ICountry,
          );
        } else {
          setCountry(
            _.find(countries, [
              'isoCode',
              process.env.REACT_APP_COUNTRYCODE,
            ]) as ICountry,
          );
        }
      }
    }
  }, [countries, countryIsoCode]);
  const langRef = useRef<string>(lang);
  useEffect(() => {
    if (langRef.current !== lang) {
      langRef.current = lang;
      if (lang) {
        if (!isFord) {
          import(`../../i18n/${lang}.json`)
            .then((module) => {
              setReplacements(module.default);
            })
            .catch(() => {
              import('../../i18n/ENG.json').then((module) => {
                setReplacements(module.default);
              });
            });     
        }
        else {
          import(`../../i18n/ford/${lang}.json`).then((module) => {
            setReplacements(module.default);
          }).catch(() => {
            import(`../../i18n/ford/ENG.json`).then((module) => {
              setReplacements(module.default);
            });
          });
        }
      }
    }
  }, [lang]);
  useEffect(() => {
    if (
      _.isNull(whereToBuyEnabled) ||
      _.isUndefined(whereToBuyEnabled) ||
      _.isNull(whereToServiceEnabled) ||
      _.isUndefined(whereToServiceEnabled) ||
      _.isNull(enableWtbDistanceFilter) ||
      _.isUndefined(enableWtbDistanceFilter)
    ) {
      getCountrySetting().then((d) => {
        setIsMiles(d.data.isMiles);
        setDistanceInMilesOrKM(d.data.distanceInMilesOrKM);

        setWhereToBuyEnabled(
          d.data.whereToBuyForRecommendationEnabled ?? false,
        );
        setWhereToServiceEnabled(
          d.data.whereToServiceForRecommendationEnabled ?? false,
        );
        setHasCastrolCertifiedWorkshops(
          d.data.hasCastrolCertifiedWorkshops ?? false,
        );
        setEnableWtbDistanceFilter(d.data.enableWtbDistanceFilter);
      });
    }
  }, []);

  //Location Types List
  useEffect(() => {
    if (!locationTypesList) {
      getLocationTypes({page: 0}).then((d) =>
        setLocationTypesList(d.data.items),
      );
    }
  }, [locationTypesList]);

  //Location Categories List
  useEffect(() => {
    if (!locationCategoriesList) {
      getLocationCategories().then((d) =>
        setLocationCatetoriesList(d.data.items),
      );
    }
  }, [locationCategoriesList]);

  //Product Categories List
  useEffect(() => {
    if (!productCategoriesList) {
      getProductCategories().then((d) =>
        setProductCatetoriesList(d.data.items),
      );
    }
  }, [productCategoriesList]);

  const [searchParam] = useSearchParams();
  const languageFromParam = searchParam.get('language');
  const variant = searchParam.get('variant');
  const isFord = variant === Variant.FORD;  
  const languageFromPath = useLocation().pathname.split('/')[1];
  const finalLang = languageIsoCode
    ? languageIsoCode
    : localStorage.getItem('languageCode')
    ? localStorage.getItem('languageCode')
    : process.env.REACT_APP_COUNTRYCODE;
  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (localStorage.getItem('languageCode')) {
      setLang(localStorage.getItem('languageCode') as string);
    } else {
      if (languageFromPath || languageFromParam) {
        if (languageIsoCode) {
          setLang(languageIsoCode);
        } else {
          setLang(process.env.REACT_APP_LANGUAGECODE as string);
        }
      } else {
        setLang(process.env.REACT_APP_LANGUAGECODE as string);
      }
    }
    // const r = _.find(GLOBALFONTFAMILY, (item) => {
    //   return _.includes(item.countries, finalLang);
    // });
    const r = {
      fontFamily: window.getComputedStyle(document.body, null).getPropertyValue( "font-family" )
    };
    (document.querySelector('.wtb-outlet') as HTMLElement)?.style?.setProperty(
      '--font-family',
      r?.fontFamily ? r?.fontFamily : 'CastrolSans',
    );
    const timeoutHandler = setInterval(() => {
      const ele = document.querySelector('.new-ui');
      if (ele) {
        (ele as HTMLElement)?.style?.setProperty(
          '--font-family',
          r.fontFamily === 'Times' ? 'CastrolSans' : r.fontFamily,
        );
        clearInterval(timeoutHandler);
      }
    }, 100);
  }, [languageIsoCode, languageFromParam, languageFromPath]);

  useEffect(() => {
    if (_.isEmpty(countries)) {
      setCountries(COUNTRIES);
    }
    if (_.isEmpty(langs)) {
      setLangs(LANGUAGES);
    }
  }, []);

  return (
    <>
      {_.isEmpty(replacements) ? (
        <>
          <Header />
          <Loading />
        </>
      ) : (
        children
      )}
    </>
  );
}

const mapStateToProps = (state: IState) => ({
  lang: state.lang,
  langs: state.langs,
  replacements: state.replacements,
  countries: state.countries,
  whereToBuyEnabled: state.whereToBuyEnabled,
  whereToServiceEnabled: state.whereToServiceEnabled,
  hasCastrolCertifiedWorkshops: state.hasCastrolCertifiedWorkshops,
  locationTypesList: state.locationTypesList,
  locationCategoriesList: state.locationCategoriesList,
  productCategoriesList: state.productCategoriesList,
  enableWtbDistanceFilter: state.enableWtbDistanceFilter,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setLang: (lang: string) => dispatch(actions.setLang(lang)),
  setLangs: (langs: ILang[]) => dispatch(actions.setLangs(langs)),
  setReplacements: (value: Replacement) =>
    dispatch(actions.setReplacements(value)),
  setCountry: (country: ICountry) => dispatch(actions.setCountry(country)),
  setCountries: (value: ICountry[]) => dispatch(actions.setCountries(value)),
  setWhereToBuyEnabled: (value: boolean) =>
    dispatch(actions.setWhereToBuyEnabled(value)),
  setWhereToServiceEnabled: (value: boolean) =>
    dispatch(actions.setWhereToServiceEnabled(value)),
  setEnableWtbDistanceFilter: (value: boolean) =>
    dispatch(actions.setEnableWtbDistanceFilter(value)),
  setIsMiles: (value: boolean) => dispatch(actions.setIsMiles(value)),
  setDistanceInMilesOrKM: (value: number) =>
    dispatch(actions.setDistanceInMilesOrKM(value)),
  setHasCastrolCertifiedWorkshops: (value: boolean) =>
    dispatch(actions.setHasCastrolCertifiedWorkshops(value)),
  setLocationTypesList: (value: LocationType[]) =>
    dispatch(actions.setLocationTypesList(value)),
  setLocationCatetoriesList: (value: LocationCategory[]) =>
    dispatch(actions.setLocationCatetoriesList(value)),
  setProductCatetoriesList: (value: ProductCategory[]) =>
    dispatch(actions.setProductCatetoriesList(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WithLangs);
